import { CirclePlus, Tag } from 'lucide-react';
import {
  type FormEvent,
  type ReactNode,
  useDeferredValue,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { FormError } from './components';
import { useAllTags, useCreateTag, useUpdateTag } from './hooks';

export type TagPickerProps = {
  /** Selected tag ids, in selection order. */
  value: string[];
  /**
   * Whose tags to offer: a client's (the entry's project's client), null for tags without a client,
   * or undefined for every tag (each shown with its client name; no create).
   */
  clientId: string | null | undefined;
  /**
   * The new selection, once, when the dropdown closes (and only if it changed). While it is open
   * the picker shows the ticked tags itself, so ticking several tags saves an entry once.
   */
  onChange: (value: string[]) => void;
  /**
   * false: call onChange on every tick instead, for forms that only keep the value until their own
   * Save (whose button may depend on it, e.g. tags a client requires).
   */
  commitOnClose?: boolean;
  placeholder?: string;
  /** Show "Create new tag" (Clockify lets anyone create tags from the tracker). */
  allowCreate?: boolean;
  /** Offer to restore an archived tag whose name is typed (needs catalog:manage). */
  allowRestore?: boolean;
  disabled?: boolean;
  className?: string;
  /** Names to show for `value` until the tag list has loaded (e.g. from a report row). */
  fallbackNames?: string[];
  /** Highlights the field as missing (tags the client requires). */
  invalid?: boolean;
};

const sameTags = (a: string[], b: string[]) => a.length === b.length && a.every((id) => b.includes(id));

/**
 * Clockify's tag dropdown: search, a checkbox per tag and "Create new tag". Stays open while
 * toggling. Every picker reads the one cached list of all tags and filters its client's in memory.
 */
export function TagPicker({
  value,
  clientId,
  onChange,
  placeholder = 'Tags',
  allowCreate = true,
  allowRestore = false,
  disabled,
  className,
  fallbackNames,
  invalid,
  commitOnClose = true,
}: TagPickerProps) {
  const [open, setOpen] = useState(false);
  // The selection while the dropdown is open (null when closed, or with commitOnClose off): shown at
  // once, committed by onChange when it closes.
  const [draft, setDraft] = useState<string[] | null>(null);
  const [search, setSearch] = useState('');
  const [naming, setNaming] = useState(false);
  const [newName, setNewName] = useState('');
  const newNameId = useId();
  const scoped = clientId !== undefined;
  // Every tag, archived ones too (existing selections keep their names), shared by all pickers.
  const tags = useAllTags();
  const ready = tags.data !== undefined;
  const selected = draft ?? value;
  const create = useCreateTag();
  const restore = useUpdateTag();

  // The latest props and draft, for changes that land later (a tag created after the dropdown
  // closed) and for a picker that goes away while open (its ticks are still committed).
  const live = useRef({ open, value, onChange, draft });
  useLayoutEffect(() => {
    Object.assign(live.current, { value, onChange, draft });
  });
  useLayoutEffect(
    () => () => {
      const { draft: pending, value: current, onChange: commit } = live.current;
      if (pending && !sameTags(pending, current)) commit(pending);
    },
    [],
  );

  const { own, byId, nameKey } = useMemo(() => {
    const list = tags.data ?? [];
    return {
      // The client's own tags (the API's order: by name).
      own: scoped ? list.filter((t) => t.clientId === (clientId ?? null)) : list,
      byId: new Map(list.map((t) => [t.id, t])),
      nameKey: new Map(list.map((t) => [t.id, t.name.toLowerCase()])),
    };
  }, [tags.data, scoped, clientId]);
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  // Selected tags of another client (e.g. the project just changed) keep their names, flagged.
  const foreign = useMemo(() => {
    if (!scoped) return [];
    const ownIds = new Set(own.map((t) => t.id));
    if (selected.every((id) => ownIds.has(id))) return [];
    return (tags.data ?? []).filter((t) => selectedSet.has(t.id) && !ownIds.has(t.id));
  }, [scoped, own, selected, selectedSet, tags.data]);
  const foreignIds = useMemo(() => new Set(foreign.map((t) => t.id)), [foreign]);
  const findByName = (name: string) => {
    const key = name.trim().toLowerCase();
    return own.find((t) => nameKey.get(t.id) === key);
  };

  // Shown in the order they were picked: the first one, then "+N" for the rest.
  const knownNames = selected.map((id) => byId.get(id)?.name).filter((n): n is string => n !== undefined);
  const selectedNames =
    ready && (knownNames.length === selected.length || !fallbackNames) ? knownNames : (fallbackNames ?? []);
  const allNames = selectedNames.join(', ');
  const q = search.trim().toLowerCase();
  // Typing stays responsive with many tags: the list follows the search a beat later if need be.
  const listQ = useDeferredValue(q);
  const visible = useMemo(
    () =>
      [...foreign, ...own].filter(
        (t) => (!t.archived || selectedSet.has(t.id)) && (!listQ || nameKey.get(t.id)!.includes(listQ)),
      ),
    [foreign, own, selectedSet, listQ, nameKey],
  );

  // Typing the name of an archived tag (that isn't already selected) offers Restore instead of Create.
  const archivedMatch = q
    ? own.find((t) => t.archived && nameKey.get(t.id) === q && !selectedSet.has(t.id))
    : undefined;

  /** Changes the open dropdown's selection, or (closed, or commitOnClose off) the value itself. */
  const change = (edit: (ids: string[]) => string[]) => {
    const { open: isOpen, value: current, onChange: commit } = live.current;
    if (isOpen && commitOnClose) return setDraft((d) => edit(d ?? current));
    const next = edit(current);
    if (!sameTags(next, current)) commit(next);
  };
  const toggle = (id: string) =>
    change((ids) => (ids.includes(id) ? ids.filter((v) => v !== id) : [...ids, id]));

  const select = (id: string) => {
    change((ids) => (ids.includes(id) ? ids : [...ids, id]));
    setSearch('');
    setNewName('');
    setNaming(false);
  };

  // Names are unique case-insensitively, so an existing tag is selected rather than re-created.
  // (The mutations' own error state shows a failure; the promises only report success.)
  const createTag = (name: string) => {
    const existing = findByName(name);
    if (existing && (!existing.archived || selectedSet.has(existing.id))) return select(existing.id);
    create.mutateAsync({ name, clientId: clientId ?? null }).then((tag) => select(tag.id), ignore);
  };

  const restoreTag = (id: string) =>
    restore.mutateAsync({ id, archived: false }).then(() => select(id), ignore);

  const setOpenState = (next: boolean) => {
    live.current.open = next;
    setOpen(next);
    if (next) {
      if (commitOnClose) setDraft(value);
      // Tags are cached for minutes: pick up ones added elsewhere since (the list shows meanwhile).
      if (tags.isStale && !tags.isFetching) void tags.refetch();
      return;
    }
    live.current.draft = null; // committed here, not again if the picker unmounts at once
    if (draft && !sameTags(draft, value)) onChange(draft);
    setDraft(null);
    setSearch('');
    setNaming(false);
    create.reset();
    restore.reset();
  };

  const onCreateClick = () => {
    create.reset();
    if (search.trim()) createTag(search);
    else setNaming(true);
  };
  const submitNew = (e: FormEvent) => {
    e.preventDefault();
    if (newName.trim()) createTag(newName);
  };

  return (
    <Popover open={open} onOpenChange={setOpenState}>
      <TagsTooltip text={allNames}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            disabled={disabled}
            aria-label={selectedNames.length ? `${placeholder}: ${allNames}` : placeholder}
            aria-invalid={invalid || undefined}
            className={cn(
              'max-w-full justify-start gap-1.5 px-2 font-normal',
              invalid && 'border border-destructive',
              className,
            )}
          >
            <Tag
              className={cn(
                'size-4',
                invalid
                  ? 'text-destructive'
                  : selectedNames.length
                    ? 'text-primary'
                    : 'text-muted-foreground',
              )}
            />
            {selectedNames.length > 0 && (
              <span className="min-w-0 max-w-28 truncate text-primary">{selectedNames[0]}</span>
            )}
            {selectedNames.length > 1 && (
              <span className="shrink-0 rounded-full bg-primary/10 px-1.5 font-medium text-[11px] text-primary leading-5">
                +{selectedNames.length - 1}
              </span>
            )}
          </Button>
        </PopoverTrigger>
      </TagsTooltip>
      <PopoverContent aria-label="Select tags" align="start" className="w-72 p-0">
        <div className="border-b p-2">
          <Input
            placeholder="Add/Find tags..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              // A create error is about the name being typed: it goes once the name changes.
              if (create.isError) create.reset();
            }}
            autoFocus
          />
        </div>
        <div className="max-h-72 min-h-0 overflow-y-auto py-1">
          {!ready && <p className="px-3 py-2 text-muted-foreground text-sm">Loading…</p>}
          {ready && visible.length === 0 && (
            <p className="px-3 py-2 text-muted-foreground text-sm">No matching tags</p>
          )}
          {visible.map((t) => {
            const id = `tag-${t.id}`;
            return (
              <div key={t.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-accent">
                <Checkbox
                  id={id}
                  aria-label={t.name}
                  checked={selectedSet.has(t.id)}
                  onCheckedChange={() => toggle(t.id)}
                />
                <label htmlFor={id} className="flex min-w-0 flex-1 cursor-pointer gap-1.5 text-sm">
                  <span className="truncate">{t.name}</span>
                  {!scoped && (
                    <span className="ml-auto shrink-0 text-muted-foreground text-xs">
                      {t.clientName ?? 'No client'}
                    </span>
                  )}
                  {foreignIds.has(t.id) && (
                    <span
                      className="ml-auto shrink-0 text-destructive text-xs"
                      title="This tag belongs to another client and can't stay on this entry"
                    >
                      Other client
                    </span>
                  )}
                </label>
              </div>
            );
          })}
        </div>
        {allowCreate && scoped && (
          <div className="grid gap-2 border-t p-1">
            {archivedMatch ? (
              <div className="grid gap-1 px-2 py-1.5">
                <p className="text-muted-foreground text-sm">Tag "{archivedMatch.name}" is archived</p>
                {allowRestore ? (
                  <Button
                    type="button"
                    variant="ghost"
                    className="justify-start text-primary"
                    disabled={restore.isPending}
                    onClick={() => restoreTag(archivedMatch.id)}
                  >
                    Restore
                  </Button>
                ) : (
                  <Button type="button" variant="ghost" className="justify-start text-primary" disabled>
                    <CirclePlus /> Create new tag
                  </Button>
                )}
              </div>
            ) : naming ? (
              <form onSubmit={submitNew} className="flex gap-2 p-1">
                <label htmlFor={newNameId} className="sr-only">
                  New tag name
                </label>
                <Input
                  id={newNameId}
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  autoFocus
                />
                <Button type="submit" disabled={create.isPending || !newName.trim()}>
                  ADD
                </Button>
              </form>
            ) : (
              <Button
                type="button"
                variant="ghost"
                className="w-full justify-start text-primary"
                disabled={create.isPending}
                onClick={onCreateClick}
              >
                <CirclePlus /> Create new tag
              </Button>
            )}
            {(create.error || restore.error) && (
              <div className="px-2 pb-1">
                <FormError error={create.error ?? restore.error} />
              </div>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

const ignore = () => {};

/**
 * Every selected tag, comma-separated, on hover of the trigger (which shows only the first). It
 * hangs off a wrapper, so the tooltip and the popover don't fight over the same button.
 */
function TagsTooltip({ text, children }: { text: string; children: ReactNode }) {
  // Always the same elements (only kept closed without tags), so the trigger never remounts.
  return (
    <Tooltip open={text ? undefined : false}>
      <TooltipTrigger asChild>
        <span className="inline-flex min-w-0 max-w-full">{children}</span>
      </TooltipTrigger>
      <TooltipContent>{text}</TooltipContent>
    </Tooltip>
  );
}
